import { useEffect, useState, useCallback } from 'react';
import useTrimLevelsQuery from '../../../trimLevels/hooks/queries/useTrimLevelsQuery';
import TrimLevelType from '../../../trimLevels/enum/TrimLevelType';
import useUrlSearchParams from '../useUrlSearchParams';
import { UrlSearchParamType } from '../../enum/UrlSearchParamType';
import useCustomizeCarContext from '../useCustomizeCarContext';
import usePlatformConfig from '../../../common/react/usePlatformConfig';

interface IUseChangeVariantReturn {
  selectedVariantId: string;
  changeVariant: (variantId: string) => void;
  trimLevelType: string;
}

export const useChangeVariant = (): IUseChangeVariantReturn => {
  const {
    platformConfig: { isDealerSelectionEnabled },
  } = usePlatformConfig();
  const { data: trimLevel } = useTrimLevelsQuery();
  const trimLevelType = trimLevel?.trimLevels[0]?.type;
  const { optimisticSelectedFeatures } = useCustomizeCarContext();
  const { searchParams, setUrlSearchParams } = useUrlSearchParams();
  const trimLevelParam = trimLevelType === TrimLevelType.VARIANT ? TrimLevelType.VARIANT_ID : trimLevelType;
  const oldVariantId = searchParams?.[trimLevelParam as UrlSearchParamType];
  const postcode = searchParams?.postcode;
  const marketingCategory = searchParams?.marketingCategory;
  const [selectedVariantId, setSelectedVariantId] = useState<string>(oldVariantId as string);

  const changeVariant = useCallback(
    (variantId: string) => {
      if (variantId === oldVariantId) {
        return;
      }

      setSelectedVariantId(variantId);

      setUrlSearchParams({
        ...(isDealerSelectionEnabled && {
          postcode,
        }),
        marketingCategory,
        [trimLevelParam]: variantId,
        selectedFeatures: optimisticSelectedFeatures,
      });
    },
    [
      setUrlSearchParams,
      marketingCategory,
      isDealerSelectionEnabled,
      postcode,
      trimLevelParam,
      oldVariantId,
      optimisticSelectedFeatures,
    ],
  );

  useEffect(() => {
    if (oldVariantId) {
      setSelectedVariantId(oldVariantId as string);
    }
  }, [oldVariantId]);

  return { selectedVariantId, changeVariant, trimLevelType };
};
